import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedFTPServer } from '../Shared/ManagedFTPServer';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminOperation,
    ManagedAdminEditorMode,
    ManagedActionModalMode,
    ManagedAdminActionMode,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapFTPServerAdminModel,
} from '../Shared/ManagedHelpers';
import Administration from '../../Constants/Endpoints/Administration';
import GetFTPServerAdminModelsRequest from '../../Interfaces/Requests/Administration/GetFTPServerAdminModelsRequest';
import GetFTPServerAdminModelsResponse from '../../Interfaces/Responses/Administration/GetFTPServerAdminModelResponse';
import AUDFTPServerRequest from '../../Interfaces/Requests/Administration/AUDFTPServerRequest';
import {
    AddIcon,
    Button,
    Card,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAIExplainModelResponse from '../../Interfaces/Responses/AI/GetAIExplainModelResponse';
import GetAIFTPServerExplainModelRequest from '../../Interfaces/Requests/AI/GetAIFTPServerExplainModelRequest';

type AUDFTPServerFormValues = {
    fsDetailsName: string;
    fsDetailsUserName: string;
    fsDetailsPassword: string;
    fsDetailsServer: string;
    fsDetailsPort: number;
    fsDetailsRemoteFolder: string;
    fsDetailsDelimiter: string;
    fsDetailsPGPKeyName: string;
    fsDetailsFileNameQuery: string;
    fsDetailsIncludeColumnHeaders: boolean;
    fsDetailsIsSFTP: boolean;
};

type AUDFTPServerProps = {};

const AUDFTPServer: React.FC<AUDFTPServerProps> = () => {
    const [formAUDFTPServers] = Form.useForm();

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({ open: false, mode: 'info', title: '', message: '', isError: false, });

    const [addButtonClicked, setAddButtonClicked] = useState(true);
    const [updButtonClicked, setUpdButtonClicked] = useState(false);
    const [delButtonClicked, setDelButtonClicked] = useState(false);

    const [fsSelectedFTPServerID, setFSSelectedFTPServerID] = useState(-1);
    const [fsSelectedFTPServerName, setFSSelectedFTPServerName] = useState('');
    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredFSField = (fieldName: string): Promise<void> => {
        const value = formAUDFTPServers.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateFSDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'fsDetailsName':
            case 'fsDetailsUserName':
            case 'fsDetailsServer':
            case 'fsDetailsRemoteFolder':
            case 'fsDetailsDelimiter':
            case 'fsDetailsFileNameQuery':
                return validateRequiredFSField(rule.field);

            case 'fsDetailsPassword':
                if (addButtonClicked) {
                    return validateRequiredFSField(rule.field);
                }
                return Promise.resolve();

            case 'fsDetailsPort': {
                const portValue = formAUDFTPServers.getFieldValue('fsDetailsPort');
                return isBlank(portValue) ? Promise.reject() : Promise.resolve();
            }

            case 'fsDetailsPGPKeyName':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const setFSMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getFSOperation = (): ManagedAdminOperation => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Upd';
        if (delButtonClicked) return 'Del';
        return '';
    };

    const getFSActionModalMode = (): ManagedActionModalMode => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        if (delButtonClicked) return 'delete';
        return 'info';
    };

    const getFSActionLabel = (): string => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Update';
        if (delButtonClicked) return 'Delete';
        return 'Manage';
    };

    const getFSActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const getFSActionTargetName = (): string => {
        return fsSelectedFTPServerName || formAUDFTPServers.getFieldValue('fsDetailsName') || '';
    };

    const showFSInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const buildFSErrorMessage = (prefix: string, err: unknown): string => {
        const apiError = err as ManagedApiError;

        return apiError?.response?.data?.message
            ? `${prefix}: ${apiError.response.data.message}`
            : `${prefix}: ${String(err)}`;
    };

    const showFSResultModal = (isError: boolean, message: string) => {
        setManageActionModalState(buildManagedActionModalState(getFSActionModalMode(), `${getFSActionLabel()} FTP Server`, message, isError));
    };

    const clearFSSelection = () => {
        setFSSelectedFTPServerID(-1);
        setFSSelectedFTPServerName('');
        setFSMode('add');
        setFSBlankForm();
    };

    const buildAUDFTPServerRequest = (): AUDFTPServerRequest => {
        const fsDetailsName = formAUDFTPServers.getFieldValue('fsDetailsName');
        const fsDetailsUserName = formAUDFTPServers.getFieldValue('fsDetailsUserName');
        const fsDetailsPassword = formAUDFTPServers.getFieldValue('fsDetailsPassword');
        const fsDetailsServer = formAUDFTPServers.getFieldValue('fsDetailsServer');
        const fsDetailsPort = Number(formAUDFTPServers.getFieldValue('fsDetailsPort'));
        const fsDetailsRemoteFolder = formAUDFTPServers.getFieldValue('fsDetailsRemoteFolder');
        const fsDetailsDelimiter = formAUDFTPServers.getFieldValue('fsDetailsDelimiter');
        const fsDetailsPGPKeyName = formAUDFTPServers.getFieldValue('fsDetailsPGPKeyName');
        const fsDetailsFileNameQuery = formAUDFTPServers.getFieldValue('fsDetailsFileNameQuery');
        const fsDetailsIsSFTP = formAUDFTPServers.getFieldValue('fsDetailsIsSFTP');
        const fsDetailsIncludeColumnHeaders = formAUDFTPServers.getFieldValue('fsDetailsIncludeColumnHeaders');

        return {
            id: fsSelectedFTPServerID,
            name: fsDetailsName ?? '',
            server: fsDetailsServer ?? '',
            port: Number.isFinite(fsDetailsPort) ? fsDetailsPort : 0,
            userName: fsDetailsUserName ?? '',
            password: fsDetailsPassword ?? '',
            isSFTP: fsDetailsIsSFTP ?? false,
            format: 0,
            delimiter: fsDetailsDelimiter ?? '',
            fixedWidthLayout: '',
            includeColumnHeaders: fsDetailsIncludeColumnHeaders ?? false,
            remoteFolder: fsDetailsRemoteFolder ?? '',
            fileNameQuery: fsDetailsFileNameQuery ?? '',
            pgp_Key_Name: fsDetailsPGPKeyName ?? '',
            operation: getFSOperation(),
        };
    };

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-ftp-server-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_FTP_SERVER_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_FTP_SERVER_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManageActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for FTP Server Explain.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = 'FTP Server Explain';

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase.toLowerCase()) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase.toLowerCase())
        );
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, ftpServerID, prompt }: GetAIFTPServerExplainModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AIFTPServerExplain`;
                const requestData: GetAIFTPServerExplainModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    ftpServerID: ftpServerID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAIExplainModelResponse, GetAIFTPServerExplainModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_FTP_SERVER_AI_CONTEXT_ASSIST: [AI FTP Server Explain] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIExplainModelResponse) => {
                setAIContextAssistResultText((response.text ?? '').trim());
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI FTP Server Explain returned successfully.' : 'AI FTP Server Explain request did not succeed.',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_FTP_SERVER_AI_CONTEXT_ASSIST: [AI FTP Server Explain] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'Unable to complete the AI FTP Server Explain request.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { data: ftpServers = [], isLoading: isGetFTPServersLoading, refetch: refetchFTPServers, } = useQuery<GetFTPServerAdminModelsResponse[], Error, ManagedFTPServer[]>(
        ['ftpServerAdminModels'],
        async (): Promise<GetFTPServerAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_FTPSERVER_ADMIN_MODELS}`;
                const requestData: GetFTPServerAdminModelsRequest = {};
                return await AxiosUtil.get<GetFTPServerAdminModelsResponse[], GetFTPServerAdminModelsRequest>(url, requestData);
            } catch (err) {
                console.error('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ', err);
                showFSInfoModal('FTP Server Load Error', buildFSErrorMessage('Unable to load FTP Servers for Manage FTP Servers.', err));
            },
            select: (response: GetFTPServerAdminModelsResponse[]): ManagedFTPServer[] =>
                response
                    .map(mapFTPServerAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name)),
        }
    );

    const { mutate: audFTPServer, isLoading: isAUDFTPServerLoading, reset: resetAUDFTPServer } = useMutation<boolean, Error, void>(
        async (): Promise<boolean> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.AUD_FTPSERVER_DETAILS_BY_ID}`;
                const requestData: AUDFTPServerRequest = buildAUDFTPServerRequest();
                const responseData = await AxiosUtil.post<boolean, AUDFTPServerRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUD_FTPSERVER_DETAILS_BY_ID: [AUD FTP Server t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                const ftpServerName = getFSActionTargetName();
                console.error('AUD_FTPSERVER_DETAILS_BY_ID: [AUD FTP Server o/e] Internal Error: ', err);
                showFSResultModal(true, `${getFSActionLabel()} FTP Server: ${ftpServerName} Failed. Please Contact Application Support`);
            },
            onSuccess: async (response: boolean) => {
                const ftpServerName = getFSActionTargetName();
                const actionLabel = getFSActionLabel();

                if (delButtonClicked) {
                    clearFSSelection();
                    await refetchFTPServers();
                    showFSResultModal(false, `${actionLabel} FTP Server: ${ftpServerName} Succeeded.`);
                    return;
                }

                const refetchResult = await refetchFTPServers();
                const refreshedFTPServer = refetchResult.data?.find((eachFTPServer) => eachFTPServer.id === fsSelectedFTPServerID);

                if (refreshedFTPServer) {
                    setFSFormFromSelectedFTPServer(refreshedFTPServer);
                    setFSSelectedFTPServerName(refreshedFTPServer.name);
                }

                showFSResultModal(false, `${actionLabel} FTP Server: ${ftpServerName} Succeeded.`);
            },
        }
    );

    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo<AUDFTPServerFormValues>) => {
        showFSInfoModal('FTP Server Validation Error', 'Please correct the highlighted FTP Server fields and try again.');
    };

    const onFinish = () => {
        audFTPServer();
    };

    const buildFTPServerExplainSelectionSummary = (selectedFTPServer: ManagedFTPServer | undefined): string => {
        if (!selectedFTPServer) {
            return '';
        }

        return [
            `FTP Server ID: ${selectedFTPServer.id}`,
            `FTP Server Name: ${selectedFTPServer.name}`,
            '',
            'The detailed FTP Server Explain context is built by the backend.',
        ].join('\n');
    };

    const handleOpenAIFTPServerExplainModal = () => {
        const selectedFTPServer = getSelectedFTPServer();

        setAIContextAssistCurrentText(buildFTPServerExplainSelectionSummary(selectedFTPServer));
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                'Unable to find an AI Provider matching FTP Server Explain.',
            ]);
            return;
        }

        if (fsSelectedFTPServerID <= 0) {
            setAIContextAssistAncillaryItems([
                'Select an FTP Server before asking AI to explain it.',
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            ftpServerID: fsSelectedFTPServerID,
            prompt: requestText,
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const getSelectedFTPServer = (selectedFTPServerID: number = fsSelectedFTPServerID): ManagedFTPServer | undefined => {
        return ftpServers.find((ftpServer) => ftpServer.id === selectedFTPServerID);
    };

    const handleFSSelectionChange = (value: number) => {
        setFSSelectedFTPServerID(value);

        const selectedFTPServer = getSelectedFTPServer(value);

        setFSSelectedFTPServerName(selectedFTPServer?.name ?? '');

        setFSMode('add');
        setFSBlankForm();
    };

    const setFSBlankForm = () => {
        formAUDFTPServers.setFieldValue('fsDetailsName', '');
        formAUDFTPServers.setFieldValue('fsDetailsUserName', '');
        formAUDFTPServers.setFieldValue('fsDetailsPassword', '');
        formAUDFTPServers.setFieldValue('fsDetailsServer', '');
        formAUDFTPServers.setFieldValue('fsDetailsPort', 0);
        formAUDFTPServers.setFieldValue('fsDetailsRemoteFolder', '');
        formAUDFTPServers.setFieldValue('fsDetailsDelimiter', '');
        formAUDFTPServers.setFieldValue('fsDetailsPGPKeyName', '');
        formAUDFTPServers.setFieldValue('fsDetailsFileNameQuery', '');
        formAUDFTPServers.setFieldValue('fsDetailsIncludeColumnHeaders', false);
        formAUDFTPServers.setFieldValue('fsDetailsIsSFTP', false);
    };

    const setFSFormFromSelectedFTPServer = (selectedFTPServer: ManagedFTPServer | undefined = getSelectedFTPServer()) => {
        formAUDFTPServers.setFieldValue('fsDetailsName', selectedFTPServer?.name ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsUserName', selectedFTPServer?.userName ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsPassword', '');
        formAUDFTPServers.setFieldValue('fsDetailsServer', selectedFTPServer?.server ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsPort', selectedFTPServer?.port ?? 0);
        formAUDFTPServers.setFieldValue('fsDetailsRemoteFolder', selectedFTPServer?.remoteFolder ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsDelimiter', selectedFTPServer?.delimiter ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsPGPKeyName', selectedFTPServer?.pgp_Key_Name ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsFileNameQuery', selectedFTPServer?.fileNameQuery ?? '');
        formAUDFTPServers.setFieldValue('fsDetailsIncludeColumnHeaders', selectedFTPServer?.includeColumnHeaders ?? false);
        formAUDFTPServers.setFieldValue('fsDetailsIsSFTP', selectedFTPServer?.isSFTP ?? false);
    };

    const handleFSFTPServerResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setFSBlankForm();
            return;
        }

        setFSFormFromSelectedFTPServer();
    };

    //=================================================================
    //=================================================================
    
    const handleFSAddFTPServer = () => {
        resetAUDFTPServer();
        clearFSSelection();
    };

    //=================================================================
    //=================================================================

    const handleFSUpdFTPServer = () => {
        resetAUDFTPServer();
        setFSMode('update');
        handleFSFTPServerResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleFSDelFTPServer = () => {
        resetAUDFTPServer();
        setFSMode('delete');
        handleFSFTPServerResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleManageModalOk = () => {
        setManageActionModalState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const onFSCancel = () => {
        resetAUDFTPServer();
        clearFSSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '8px',
        height: '470px'
    };
    
    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageModalOk}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title='AI FTP Server Explain'
                currentSectionTitle='Current FTP Server Context'
                requestSectionTitle='What do you want explained?'
                resultSectionTitle='FTP Server Explanation'
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder='Optionally describe what part of this FTP Server administration record you want explained.'
                askButtonText='Explain FTP Server'
                useButtonText='Use Suggestion'
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                allowUseSuggestion={false}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={() => undefined}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <Spin spinning={
                            isGetFTPServersLoading ||
                            isAUDFTPServerLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage FTP Servers</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{ float: 'right' }}>Select FTP Server to Update or Delete:</Text>
                    </Col>
                    <Col span={10}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select an FTP Server'}
                            showSearch
                            style={{paddingTop: '4px', width: '100%'}}
                            value={fsSelectedFTPServerID === -1 ? undefined : fsSelectedFTPServerID}
                            onChange={handleFSSelectionChange}
                        >
                            {ftpServers.map(
                                (eachFTPServer) => {
                                    return (
                                        <Option
                                            key={eachFTPServer.id}
                                            label={eachFTPServer.name}
                                            value={eachFTPServer.id}
                                        >
                                            {eachFTPServer.name + ' (' + eachFTPServer.server + ')'}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                    <Col span={1}>
                        <Button
                            disabled={fsSelectedFTPServerID === -1 || isGetAIProvidersLoading}
                            icon={<BulbOutlinedIlluminated />}
                            style={{
                                minWidth: '32px',
                                padding: '0px',
                                marginTop: '4px',
                            }}
                            title='AI FTP Server Explain'
                            type='default'
                            onClick={handleOpenAIFTPServerExplainModal}
                        />
                    </Col>
                    <Col span={3} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={3}>
                        <Button
                            icon={<AddIcon />}
                            style={{width: '95%', marginBottom: '4px', float: 'left',}}
                            type='primary'
                            onClick={handleFSAddFTPServer}
                        >
                            Add FTP Server
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={3}>
                        <Button
                            disabled={fsSelectedFTPServerID === -1}
                            icon={<EditIcon />}
                            style={{width: '95%', backgroundColor: 'darkgray', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleFSUpdFTPServer}
                        >
                            Update FTP Server
                        </Button>
                    </Col>
                    <Col span={3}>
                        <Button
                            disabled={fsSelectedFTPServerID === -1}
                            icon={<DeleteIcon />}
                            style={{width: '95%', backgroundColor: 'red', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleFSDelFTPServer}
                        >
                            Delete FTP Server
                        </Button>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDFTPServers}
                    initialValues={{
                        fsDetailsIncludeColumnHeaders: false,
                        fsDetailsIsSFTP: false
                    }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div style={contentStyle}>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getFSActionColor() }}>
                                            {addButtonClicked ? 'Add FTP Server' :
                                                (updButtonClicked ? 'Update FTP Server: [' + fsSelectedFTPServerID + '] ' + fsSelectedFTPServerName :
                                                    (delButtonClicked ? 'Delete FTP Server: [' + fsSelectedFTPServerID + '] ' + fsSelectedFTPServerName : ''))}
                                        </Divider>
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            style={{ color: getFSActionColor(), fontStyle: 'italic' }}
                                            type='link'
                                            onClick={() => handleFSFTPServerResetForm(addButtonClicked ? 'blank' : 'selected')}
                                        >
                                            Reset Form
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='fsdetailsname'
                                            name='fsDetailsName'
                                            label='Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Name required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='fsdetailsusername'
                                            name='fsDetailsUserName'
                                            label='User Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'User Name required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter User Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='fsdetailspassword'
                                            name='fsDetailsPassword'
                                            label='Password'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    required: addButtonClicked,
                                                    message: addButtonClicked
                                                        ? 'Password required.'
                                                        : 'Enter a password only if you want to change it.',
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder={addButtonClicked ? 'Enter Password' : 'Leave blank to keep existing password'}
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                                type='password'
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1}/>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='fsdetailsserver'
                                            name='fsDetailsServer'
                                            label='Server'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Server required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Server'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='fsdetailsport'
                                            name='fsDetailsPort'
                                            label='Port'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Port required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Port'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                                type='number'
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='fsdetailsremotefolder'
                                            name='fsDetailsRemoteFolder'
                                            label='Remote Folder'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Remote Folder required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Remote Folder'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1}/>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='fsdetailspgpkeyname'
                                            name='fsDetailsPGPKeyName'
                                            label='PGP Key Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter PGP Key Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='fsdetailsdelimiter'
                                            name='fsDetailsDelimiter'
                                            label='Delimiter'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Delimiter required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                maxLength={1}
                                                placeholder='Enter Delimiter'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='fsdetailsincludecolumnheaders'
                                            name='fsDetailsIncludeColumnHeaders'
                                            valuePropName='checked'
                                            style={{marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Include Column Headers
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={3}>
                                        <Form.Item
                                            key='fsdetailsissftp'
                                            name='fsDetailsIsSFTP'
                                            valuePropName='checked'
                                            style={{marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Is SFTP
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={1}/>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Form.Item
                                            key='fsdetailsfilenamequery'
                                            name='fsDetailsFileNameQuery'
                                            label='File Name Query'
                                            labelCol={{ span: 3, }}
                                            rules={[
                                                {
                                                    message: 'File Name Query required.',
                                                    required: true,
                                                    validator: validateFSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter File Name Query'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'right'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ visibility: addButtonClicked ? 'hidden' : 'visible' }}>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                            <Text
                                                italic
                                                style={{ color: 'gray' }}
                                            >
                                                NOTE: FTP Servers may be linked (via Foreign Key) to AutoRun Targets.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={17} />
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='fsApplyButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType='submit'
                                        style={{width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                    >
                                        {addButtonClicked ? 'Add FTP Server' : (updButtonClicked ? 'Update FTP Server' : (delButtonClicked ? 'Delete FTP Server' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='fsCancelButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        style={{width: '95%'}}
                                        type='default'
                                        onClick={onFSCancel}
                                    >
                                        {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AUDFTPServer;
